/*
 * RivanyOS Pixel — public/pixel.js
 *
 * Standalone, zero-dependency visitor-journey tracker for the May Anthony
 * Shopify storefronts (mayanthony.com / officialmay.myshopify.com = INT).
 * Embedded via theme.liquid — see docs/pixel-installation.md.
 *
 * Captures page views, collection views, product views, add-to-cart, the
 * checkout steps (Customer Events) and purchase, with UTM /
 * fbclid / ad_id / campaign_id / adset_id / referrer per touchpoint, and POSTs
 * them to the RivanyOS collector. It NEVER throws and NEVER blocks render —
 * every external touch is wrapped in try/catch and the work is deferred to
 * idle time.
 */
(function () {
  'use strict'

  var ENDPOINT = 'https://rivanyos.vercel.app/api/pixel-event'
  var RECORD_ENDPOINT = 'https://rivanyos.vercel.app/api/record-session'
  var REPLAY_CONFIG_ENDPOINT = 'https://rivanyos.vercel.app/api/session-replay-config'

  // ── session replay (rrweb) state — all per page-load, all guarded ─────────────
  var cartValue = null
  var replayEvents = []
  var replayRecording = false
  var replayStop = null
  var replayStart = Date.now()
  var replayInactivity = null
  var replayBound = false

  // ── tiny storage + uuid helpers (all guarded) ────────────────────────────────
  function uuid() {
    try {
      if (window.crypto && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
    } catch (e) {}
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0
      var v = c === 'x' ? r : (r & 0x3) | 0x8
      return v.toString(16)
    })
  }
  // localStorage first, first-party cookie as fallback. On the live storefront a
  // third-party recorder was found holding a 5 MB localStorage key, which puts
  // every other setItem over quota: without the fallback the visitor id, the
  // Meta click window and the Klaviyo token would all silently fail to persist.
  function ckGet(k) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + k + '=([^;]*)'))
      return m ? decodeURIComponent(m[1]) : null
    } catch (e) { return null }
  }
  function ckSet(k, v) {
    try { document.cookie = k + '=' + encodeURIComponent(v) + '; path=/; max-age=31536000; SameSite=Lax; Secure' } catch (e) {}
  }
  function ckDel(k) {
    try { document.cookie = k + '=; path=/; max-age=0; SameSite=Lax; Secure' } catch (e) {}
  }
  function lsGet(k) {
    var v = null
    try { v = window.localStorage.getItem(k) } catch (e) { v = null }
    return v != null ? v : ckGet(k)
  }
  function lsSet(k, v) {
    var ok = false
    try { window.localStorage.setItem(k, v); ok = window.localStorage.getItem(k) === v } catch (e) { ok = false }
    if (!ok) ckSet(k, v)
  }
  function lsDel(k) {
    try { window.localStorage.removeItem(k) } catch (e) {}
    ckDel(k)
  }
  function ssGet(k) { try { return window.sessionStorage.getItem(k) } catch (e) { return null } }
  function ssSet(k, v) { try { window.sessionStorage.setItem(k, v) } catch (e) {} }

  // ── identity ──────────────────────────────────────────────────────────────────
  // visitor_id: persists across sessions (localStorage). session_id: per tab/session.
  function visitorId() {
    var id = lsGet('rv_visitor_id')
    if (!id) { id = uuid(); lsSet('rv_visitor_id', id) }
    return id
  }
  function sessionId() {
    var id = ssGet('rv_session_id')
    if (!id) { id = uuid(); ssSet('rv_session_id', id) }
    return id
  }

  // ── URL params ────────────────────────────────────────────────────────────────
  function params() {
    try { return new URLSearchParams(window.location.search) } catch (e) { return new URLSearchParams('') }
  }

  // UTMs are captured on the landing hit and held for the whole session, so a
  // visitor who navigates away from the landing URL still carries attribution.
  function utms() {
    var stored = ssGet('rv_utms')
    if (stored) { try { return JSON.parse(stored) } catch (e) {} }
    var p = params()
    var u = {
      utm_source: p.get('utm_source'),
      utm_medium: p.get('utm_medium'),
      utm_campaign: p.get('utm_campaign'),
      utm_content: p.get('utm_content'),
      utm_term: p.get('utm_term')
    }
    if (u.utm_source || u.utm_medium || u.utm_campaign || u.utm_content || u.utm_term) {
      ssSet('rv_utms', JSON.stringify(u))
    }
    return u
  }

  // Click ids — Meta's fbclid / ad_id / campaign_id / adset_id and Google's
  // gclid / gbraid / wbraid — are captured from the URL and held for the tab
  // session only, exactly like the UTMs above. Cross-session lookback is the
  // server's job (attributionSummary.js applies one 7-day window to every
  // channel); the previous 30-day localStorage window for Meta alone re-stamped
  // Meta onto every later visit and biased attribution towards it. A fresh click
  // in the current URL overwrites the stored one for the rest of the session.
  var NO_CLICK = { fbclid: null, ad_id: null, campaign_id: null, adset_id: null, gclid: null }
  function fbData() {
    try {
      lsDel('rv_fb') // retire the old cross-session Meta key wherever it survives
      var p = params()
      var fresh = {
        fbclid: p.get('fbclid'),
        ad_id: p.get('ad_id'),
        campaign_id: p.get('campaign_id'),
        adset_id: p.get('adset_id'),
        gclid: p.get('gclid') || p.get('gbraid') || p.get('wbraid')
      }
      if (fresh.fbclid || fresh.ad_id || fresh.campaign_id || fresh.adset_id || fresh.gclid) {
        ssSet('rv_click', JSON.stringify(fresh))
        return fresh
      }
      var stored = ssGet('rv_click')
      if (stored) {
        var parsed = JSON.parse(stored)
        if (parsed && typeof parsed === 'object') return parsed
      }
      return NO_CLICK
    } catch (e) {
      return NO_CLICK
    }
  }

  // ── store detection ─────────────────────────────────────────────────────────
  // officialmay.myshopify.com and mayanthony.com are the INT store. A future NGA
  // storefront whose hostname contains "nga" resolves to NGA.
  function store() {
    var h = ''
    try { h = (window.location.hostname || '').toLowerCase() } catch (e) {}
    if (h.indexOf('nga') !== -1) return 'NGA'
    return 'INT'
  }

  // ── product handle from /products/<handle> ────────────────────────────────────
  function productHandle() {
    try {
      var m = window.location.pathname.match(/\/products\/([^/?#]+)/)
      return m ? m[1] : null
    } catch (e) { return null }
  }

  function safe(fn) { try { return fn() } catch (e) { return null } }

  // ── cart ────────────────────────────────────────────────────────────────────
  // Caches the live cart total for the replay session meta and tells the collector
  // which visitor owns this cart, so the order webhook can find them from
  // order.cart_token. Nothing is written to the cart: cart attributes show on the
  // order in Shopify admin, and carts stamped before that are cleared here.
  function linkCart(token) {
    try {
      var key = token + '|' + sessionId()
      if (ssGet('rv_cart_linked') === key) return
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify({ event_type: 'cart_link', visitor_id: visitorId(), session_id: sessionId(), cart_token: token }),
        keepalive: true,
        mode: 'cors'
      }).then(function (r) { return r.json() })
        .then(function (j) { if (j && j.ok === true) ssSet('rv_cart_linked', key) })
        .catch(function () {})
    } catch (e) {}
  }

  function refreshCart() {
    try {
      var root = '/'
      try { if (window.Shopify && Shopify.routes && Shopify.routes.root) root = Shopify.routes.root } catch (e) {}
      fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json() })
        .then(function (c) {
          if (!c) return
          if (typeof c.total_price === 'number') cartValue = c.total_price / 100
          if (c.item_count > 0 && c.token) linkCart(c.token)
          var a = c.attributes || {}
          if (a.rv_visitor_id == null && a.rv_session_id == null) return
          return fetch(root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { rv_visitor_id: '', rv_session_id: '' } })
          })
        }).catch(function () {})
    } catch (e) {}
  }

  // Klaviyo profile token. Klaviyo appends _kx=<token> to every link in its
  // emails; the token is stable per profile, so it identifies the same person
  // across devices and sessions long before they type an email at checkout.
  // Persisted in localStorage (not session) for 90 days; a fresh token in the
  // URL overwrites. Sent on every event so the drawer can union visitors on it.
  var RV_KX_WINDOW_MS = 90 * 24 * 60 * 60 * 1000
  function kxToken() {
    try {
      var fresh = params().get('_kx')
      if (fresh) {
        lsSet('rv_kx', JSON.stringify({ kx: fresh, ts: Date.now() }))
        return fresh
      }
      var stored = lsGet('rv_kx')
      if (stored) {
        var parsed = JSON.parse(stored)
        if (parsed && parsed.kx && typeof parsed.ts === 'number' && (Date.now() - parsed.ts) <= RV_KX_WINDOW_MS) return parsed.kx
        lsDel('rv_kx')
      }
    } catch (e) {}
    return null
  }

  // Shopify customer id when the visitor is logged in. Both globals are set by
  // Shopify's own storefront boot; either may be absent on a given page.
  function customerId() {
    try {
      var cid = (window.__st && window.__st.cid) || null
      if (!cid && window.ShopifyAnalytics && ShopifyAnalytics.meta && ShopifyAnalytics.meta.page) cid = ShopifyAnalytics.meta.page.customerId || null
      return cid != null && cid !== '' ? String(cid) : null
    } catch (e) { return null }
  }

  // Mirror the ids into first-party cookies on every page. Checkout pages run the
  // Customer Events pixel, which cannot see localStorage but can read these through
  // browser.cookie. Domain-wide so www and the apex share them.
  function mirrorIds() {
    try {
      var host = window.location.hostname || ''
      var scope = '; path=/; SameSite=Lax; Secure' + (/(^|\.)mayanthony\.com$/.test(host) ? '; domain=.mayanthony.com' : '')
      document.cookie = 'rv_visitor_id=' + encodeURIComponent(visitorId()) + '; max-age=31536000' + scope
      document.cookie = 'rv_session_id=' + encodeURIComponent(sessionId()) + scope
    } catch (e) {}
  }

  // ── send ──────────────────────────────────────────────────────────────────────
  function sendEvent(eventType, extra, onAccepted) {
    try {
      var u = utms()
      var f = fbData()
      var payload = {
        visitor_id: visitorId(),
        session_id: sessionId(),
        event_type: eventType,
        page_url: safe(function () { return window.location.href }),
        page_title: safe(function () { return document.title }),
        referrer: safe(function () { return document.referrer || null }),
        utm_source: u.utm_source || null,
        utm_medium: u.utm_medium || null,
        utm_campaign: u.utm_campaign || null,
        utm_content: u.utm_content || null,
        utm_term: u.utm_term || null,
        fbclid: f.fbclid || null,
        ad_id: f.ad_id || null,
        campaign_id: f.campaign_id || null,
        adset_id: f.adset_id || null,
        gclid: f.gclid || null,
        kx: kxToken(),
        customer_id: customerId(),
        product_handle: null,
        product_title: null,
        customer_email: null,
        order_id: null,
        store: store()
      }
      if (extra) {
        for (var k in extra) {
          if (Object.prototype.hasOwnProperty.call(extra, k)) payload[k] = extra[k]
        }
      }

      var body = JSON.stringify(payload)
      // Gated send: when a caller passes onAccepted, use fetch (sendBeacon has
      // no readable response) and invoke the callback only when the collector
      // accepts the event ({ ok: true }). Rejection, network error, or
      // exception: no callback, silently. Used only by the initial page_view.
      if (onAccepted) {
        try {
          fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: body,
            keepalive: true,
            mode: 'cors'
          }).then(function (r) { return r.json() })
            .then(function (j) { if (j && j.ok === true) onAccepted() })
            .catch(function () {})
        } catch (e) {}
        return
      }
      // text/plain keeps this a CORS "simple request" (no preflight) for both transports.
      var sent = false
      try {
        if (navigator.sendBeacon) {
          sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
        }
      } catch (e) { sent = false }
      if (!sent) {
        try {
          fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'text/plain' },
            body: body,
            keepalive: true,
            mode: 'cors'
          }).catch(function () {})
        } catch (e) {}
      }
    } catch (e) { /* never throw — the storefront must be unaffected */ }
  }

  // ── session replay (rrweb) ────────────────────────────────────────────────────
  // Records the DOM only after a visitor reaches a product page (conditional
  // sampling), masks all inputs, buffers events in memory, and flushes the blob to
  // the collector on pagehide or after 30s of inactivity. Like everything else
  // here it never throws and never blocks the storefront.
  function loadRrweb(cb) {
    if (window.rrweb || window.rrwebRecord) return cb()
    try {
      // Derive the theme assets base from this pixel's own <script> src,
      // so rrweb-record.js loads from the same Shopify assets directory.
      var base = ''
      try {
        var sc = document.querySelector('script[src*="pixel.js"]')
        if (sc && sc.src) base = sc.src.replace(/pixel\.js(\?.*)?$/, '')
      } catch (e) {}
      var url = base ? (base + 'rrweb-record.js') : 'rrweb-record.js'
      var s = document.createElement('script')
      s.src = url
      s.onload = cb
      s.onerror = function () {}
      document.head.appendChild(s)
    } catch (e) {}
  }

  function replayPageCount() {
    var n = parseInt(ssGet('rv_page_count') || '0', 10)
    return isNaN(n) ? 0 : n
  }

  function replayDeviceType() {
    try {
      var w = (window.screen && screen.width) ? screen.width : window.innerWidth
      return w < 768 ? 'mobile' : (w < 1024 ? 'tablet' : 'desktop')
    } catch (e) { return 'desktop' }
  }

  function postRecord(body) {
    // Large payloads (e.g. the rrweb FullSnapshot, often >64KB) exceed the
    // ~64KB cap on sendBeacon AND keepalive fetch, which silently drop them.
    // A normal fetch (no keepalive) has no size cap — use it for large bodies.
    // Small bodies keep beacon/keepalive so they survive page unload.
    var LARGE = 60000 // ~60KB, safely under the 64KB beacon/keepalive cap
    try {
      if (body.length > LARGE) {
        fetch(RECORD_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, mode: 'cors' }).catch(function () {})
        return
      }
    } catch (e) {}
    var sent = false
    try {
      if (navigator.sendBeacon) sent = navigator.sendBeacon(RECORD_ENDPOINT, new Blob([body], { type: 'text/plain' }))
    } catch (e) { sent = false }
    if (!sent) {
      try {
        fetch(RECORD_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' }).catch(function () {})
      } catch (e) {}
    }
  }

  function flushReplay() {
    try {
      if (!replayEvents.length) return
      var payload = {
        action: 'save',
        session_id: sessionId(),
        visitor_id: visitorId(),
        events: replayEvents,
        meta: {
          entry_page: ssGet('rv_entry_page') || safe(function () { return window.location.href }),
          exit_page: safe(function () { return window.location.href }),
          page_count: replayPageCount(),
          duration_seconds: Math.round((Date.now() - replayStart) / 1000),
          device_type: replayDeviceType(),
          cart_value: cartValue,
          converted: false,
          store: store()
        }
      }
      postRecord(JSON.stringify(payload))
      // Clear the buffer so the next flush only carries new events — the server
      // appends each flush onto the stored blob (multi-page sessions accumulate).
      replayEvents = []
    } catch (e) {}
  }

  function resetReplayInactivity() {
    try {
      if (replayInactivity) clearTimeout(replayInactivity)
      replayInactivity = setTimeout(function () { flushReplay() }, 30000)
    } catch (e) {}
  }

  function startReplay() {
    if (replayRecording) return
    replayRecording = true
    loadRrweb(function () {
      try {
        var rec = (window.rrweb && window.rrweb.record) ? window.rrweb.record
                  : (window.rrwebRecord && window.rrwebRecord.record) ? window.rrwebRecord.record
                  : (typeof window.rrwebRecord === 'function') ? window.rrwebRecord
                  : null
        if (!rec) { replayRecording = false; return }
        replayStart = Date.now()
        replayStop = rec({
          emit: function (event) {
            try { replayEvents.push(event); resetReplayInactivity() } catch (e) {}
          },
          maskAllInputs: true,
          maskTextSelector: 'input, textarea, select'
        })
        // Early flush: rrweb emits Meta + FullSnapshot at the start. Persist them
        // ~2s in via the large-payload (no-keepalive) fetch while the page is
        // definitely alive, so the snapshot isn't lost on a fast bounce.
        try { setTimeout(flushReplay, 2000) } catch (e) {}
        if (!replayBound) {
          replayBound = true
          try { window.addEventListener('pagehide', flushReplay) } catch (e) {}
          try {
            document.addEventListener('visibilitychange', function () {
              if (document.visibilityState === 'hidden') flushReplay()
            })
          } catch (e) {}
        }
      } catch (e) { replayRecording = false }
    })
  }

  // Fail open on IP/country exclusion checks: record unless the visitor's own
  // IP/country is explicitly excluded (the server-side collector enforces
  // exclusions authoritatively too). Fail quiet on a config-fetch failure or on
  // recording_enabled === false: do not start recording.
  function maybeStartReplay() {
    if (replayRecording) return
    try {
      fetch(REPLAY_CONFIG_ENDPOINT, { mode: 'cors' })
        .then(function (r) { return r.json() })
        .then(function (cfg) {
          try {
            if (cfg && cfg.recording_enabled === false) return
            var ips = (cfg && cfg.excluded_ips) || []
            var countries = (cfg && cfg.excluded_countries) || []
            if (cfg && cfg.ip && ips.indexOf(cfg.ip) !== -1) return
            if (cfg && cfg.country && countries.indexOf(cfg.country) !== -1) return
          } catch (e) {}
          startReplay()
        }).catch(function () {})
    } catch (e) {}
  }

  // ── public API ──────────────────────────────────────────────────────────────
  // Called from the theme (add-to-cart) and the order confirmation page (purchase).
  // Guarded for `window` so this file is safe in the Customer Events sandbox too
  // (a Web Worker context where `window` may be undefined).
  var RivanyPixel = {
    addToCart: function (productHandleArg, productTitleArg) {
      refreshCart()
      sendEvent('add_to_cart', {
        product_handle: productHandleArg || productHandle(),
        product_title: productTitleArg || null
      })
    },
    purchase: function (orderId, customerEmail) {
      sendEvent('purchase', {
        order_id: orderId != null && orderId !== '' ? String(orderId) : null,
        customer_email: customerEmail || null
      })
      // Mark the replay session converted (sets converted=true + shopify_order_id).
      try {
        postRecord(JSON.stringify({
          action: 'convert',
          session_id: sessionId(),
          visitor_id: visitorId(),
          order_id: orderId != null && orderId !== '' ? String(orderId) : null,
          customer_email: customerEmail || null
        }))
      } catch (e) {}
    }
  }
  try { if (typeof window !== 'undefined') window.RivanyPixel = RivanyPixel } catch (e) {}

  // ── Customer Events sandbox (Shopify Settings → Customer Events custom pixel) ─
  // When this file is registered as a Customer Events pixel, the sandbox exposes
  // `analytics`. In that mode we ONLY subscribe to checkout_started and forward it
  // to the collector (funnel step 4); the theme-context boot() is skipped. The
  // visitor/session ids come from the cookies written by mirrorIds().
  function inCustomerEvents() {
    try { return typeof analytics !== 'undefined' && analytics && typeof analytics.subscribe === 'function' } catch (e) { return false }
  }

  function bootCustomerEvents() {
    try {
      // Cookies from mirrorIds() win; attributes on older carts are the fallback. Each
      // handler waits for the read, so no event goes out before the ids are known.
      var readCookie = function (name) {
        try { return Promise.resolve(browser.cookie.get(name)).catch(function () { return null }) } catch (e) { return Promise.resolve(null) }
      }
      var cookieIds = Promise.all([readCookie('rv_visitor_id'), readCookie('rv_session_id')])
      var onCheckout = function (name, handler) {
        analytics.subscribe(name, function (event) {
          cookieIds.then(function (ids) { handler(event, { visitor: ids[0] || null, session: ids[1] || null }) })
        })
      }

      onCheckout('checkout_started', function (event, ids) {
        try {
          var ck = event && event.data && event.data.checkout
          var attrs = (ck && ck.attributes) || []
          var vId = null, sId = null
          for (var i = 0; i < attrs.length; i++) {
            if (attrs[i] && attrs[i].key === 'rv_visitor_id') vId = attrs[i].value
            if (attrs[i] && attrs[i].key === 'rv_session_id') sId = attrs[i].value
          }
          vId = ids.visitor || vId
          sId = ids.session || sId
          // Same ck.email read as checkout_completed below — available this early too.
          var email = (ck && ck.email) ? ck.email : null
          var body = JSON.stringify({
            visitor_id: vId || 'unknown',
            session_id: sId || 'unknown',
            event_type: 'checkout_started',
            customer_email: email,
            store: 'INT'
          })
          var sent = false
          try {
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
              sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
            }
          } catch (e) { sent = false }
          if (!sent) {
            try { fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' }).catch(function () {}) } catch (e) {}
          }
        } catch (e) {}
      })

      onCheckout('checkout_contact_info_submitted', function (event, ids) {
        try {
          var ck = event && event.data && event.data.checkout
          var attrs = (ck && ck.attributes) || []
          var vId = null, sId = null
          for (var i = 0; i < attrs.length; i++) {
            if (attrs[i] && attrs[i].key === 'rv_visitor_id') vId = attrs[i].value
            if (attrs[i] && attrs[i].key === 'rv_session_id') sId = attrs[i].value
          }
          vId = ids.visitor || vId
          sId = ids.session || sId
          // Earliest Customer Events checkout event where Shopify populates
          // checkout.email for anonymous shoppers (checkout_started is structurally
          // too early — 0/205 historical checkout_started rows ever carried one).
          var email = (ck && ck.email) ? ck.email : null
          var body = JSON.stringify({
            visitor_id: vId || 'unknown',
            session_id: sId || 'unknown',
            event_type: 'checkout_contact_info_submitted',
            customer_email: email,
            store: 'INT'
          })
          var sent = false
          try {
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
              sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
            }
          } catch (e) { sent = false }
          if (!sent) {
            try { fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' }).catch(function () {}) } catch (e) {}
          }
        } catch (e) {}
      })

      // Address and shipping steps: same shape as contact info above (ids from
      // the cookies, email when Shopify has it). Together with
      // checkout_started and contact info they give the drawer the full
      // checkout funnel per visitor rather than a single "checkout started".
      var forwardCheckoutStep = function (name) {
        onCheckout(name, function (event, ids) {
          try {
            var ck = event && event.data && event.data.checkout
            var attrs = (ck && ck.attributes) || []
            var vId = null, sId = null
            for (var i = 0; i < attrs.length; i++) {
              if (attrs[i] && attrs[i].key === 'rv_visitor_id') vId = attrs[i].value
              if (attrs[i] && attrs[i].key === 'rv_session_id') sId = attrs[i].value
            }
            vId = ids.visitor || vId
            sId = ids.session || sId
            var email = (ck && ck.email) ? ck.email : null
            var body = JSON.stringify({
              visitor_id: vId || 'unknown',
              session_id: sId || 'unknown',
              event_type: name,
              customer_email: email,
              store: 'INT'
            })
            var sent = false
            try {
              if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
              }
            } catch (e) { sent = false }
            if (!sent) {
              try { fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' }).catch(function () {}) } catch (e) {}
            }
          } catch (e) {}
        })
      }

      forwardCheckoutStep('checkout_address_info_submitted')
      forwardCheckoutStep('checkout_shipping_info_submitted')

      onCheckout('checkout_completed', function (event, ids) {
        try {
          var ck = event && event.data && event.data.checkout
          var attrs = (ck && ck.attributes) || []
          var vId = null, sId = null
          for (var i = 0; i < attrs.length; i++) {
            if (attrs[i] && attrs[i].key === 'rv_visitor_id') vId = attrs[i].value
            if (attrs[i] && attrs[i].key === 'rv_session_id') sId = attrs[i].value
          }
          vId = ids.visitor || vId
          sId = ids.session || sId
          var rawId = (ck && ck.order && ck.order.id) ? String(ck.order.id) : null
          var orderId = rawId ? rawId.replace(/^gid:\/\/shopify\/Order\//, '') : null
          var email = (ck && ck.email) ? ck.email : null
          // order.customer.id links the visitor to the Shopify customer even when
          // the shopper never logged in on the storefront.
          var rawCid = (ck && ck.order && ck.order.customer && ck.order.customer.id) ? String(ck.order.customer.id) : null
          var customerIdAtCheckout = rawCid ? rawCid.replace(/^gid:\/\/shopify\/Customer\//, '') : null
          var body = JSON.stringify({
            visitor_id: vId || 'unknown',
            session_id: sId || 'unknown',
            event_type: 'purchase',
            order_id: orderId,
            customer_email: email,
            customer_id: customerIdAtCheckout,
            store: 'INT'
          })
          var sent = false
          try {
            if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
              sent = navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'text/plain' }))
            }
          } catch (e) { sent = false }
          if (!sent) {
            try { fetch(ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: body, keepalive: true, mode: 'cors' }).catch(function () {}) } catch (e) {}
          }
          // Mark session replay as converted
          try {
            var convertBody = JSON.stringify({
              action: 'convert',
              session_id: sId || 'unknown',
              visitor_id: vId || 'unknown',
              order_id: orderId,
              customer_email: email
            })
            var cSent = false
            try {
              if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
                cSent = navigator.sendBeacon(RECORD_ENDPOINT, new Blob([convertBody], { type: 'text/plain' }))
              }
            } catch (e) { cSent = false }
            if (!cSent) {
              try { fetch(RECORD_ENDPOINT, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: convertBody, keepalive: true, mode: 'cors' }).catch(function () {}) } catch (e) {}
            }
          } catch (e) {}
        } catch (e) {}
      })
    } catch (e) {}
  }

  // ── auto-fire on load (deferred so it never blocks render) ────────────────────
  function boot() {
    mirrorIds()
    // Per-session page counter + entry page, used in the replay session meta.
    try {
      ssSet('rv_page_count', String(replayPageCount() + 1))
      if (!ssGet('rv_entry_page')) ssSet('rv_entry_page', safe(function () { return window.location.href }) || '')
    } catch (e) {}
    // Bot gate layer 1: replay starts only after this page_view is accepted by
    // the collector. On rejection, network error, or exception the recorder
    // never starts, silently.
    sendEvent('page_view', {}, function () { maybeStartReplay() })
    refreshCart()
    // Listen for Prestige theme's variant:add event (fires on every successful
    // add-to-cart via fetch to /cart/add.js, including quick-buy and PDP).
    try {
      document.addEventListener('variant:add', function (e) {
        try {
          var item = e && e.detail && e.detail.items && e.detail.items[0]
          var handle = (item && item.handle) ? item.handle : productHandle()
          var title = (item && item.product_title) ? item.product_title : null
          RivanyPixel.addToCart(handle, title)
        } catch (err) {}
      })
    } catch (e) {}
    // Collection pages get their own event alongside page_view, the same way
    // product pages do, so the journey reads "Collection view · PAWS" rather
    // than a bare page view. Path only; the collection handle is not needed.
    try {
      var cpath = window.location.pathname || ''
      if (/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i.test(cpath)) {
        sendEvent('collection_view', { page_title: safe(function () { return document.title }) })
      }
    } catch (e) {}
    var ph = productHandle()
    if (ph) {
      sendEvent('product_view', {
        product_handle: ph,
        product_title: safe(function () { return document.title })
      })
    }
  }
  try {
    if (inCustomerEvents()) {
      bootCustomerEvents()
    } else if (typeof window !== 'undefined' && window.requestIdleCallback) {
      requestIdleCallback(boot)
    } else {
      setTimeout(boot, 0)
    }
  } catch (e) {
    try { setTimeout(boot, 0) } catch (e2) {}
  }
})()
